// AUTODEX 이용가이드 페이지 (P1 + P2)
// 순수 CSS + IntersectionObserver 기반 스크롤 애니메이션
import React, { useEffect, useState } from 'react';
import useScrollReveal from './useScrollReveal';
import './UserGuide.css';

// ── 재사용 Reveal 래퍼 ──
function Reveal(props) {
  var r = useScrollReveal({ threshold: props.threshold || 0.15 });
  var style = props.delay ? { '--ug-delay': props.delay + 's' } : {};
  return (
    <div ref={r.ref} className={'ug-reveal ' + (r.visible ? 'visible' : '')} style={style}>
      {props.children}
    </div>
  );
}

// ── 빠른 시작 단계 카드 ──
function StepCard(props) {
  return (
    <div className="ug-step">
      <div className="ug-step-num">{props.num}</div>
      <div className="ug-step-icon">{props.icon}</div>
      <div className="ug-step-title">{props.title}</div>
      <p className="ug-step-desc">{props.desc}</p>
    </div>
  );
}

// ── 기능 섹션 ──
function Feature(props) {
  return (
    <Reveal>
      <div className={'ug-feature' + (props.reverse ? ' reverse' : '')}>
        <div className="ug-feature-text">
          <h3>{props.title} {props.tag && <span className="ug-tag">{props.tag}</span>}</h3>
          <p>{props.desc}</p>
          {props.bullets && (
            <ul>{props.bullets.map(function (b, i) { return <li key={i}>{b}</li>; })}</ul>
          )}
        </div>
        <div className="ug-feature-media">{props.icon}</div>
      </div>
    </Reveal>
  );
}

// ── FAQ 아이템 ──
function FAQItem(props) {
  var _s = useState(false);
  var open = _s[0];
  var setOpen = _s[1];
  return (
    <div className={'ug-faq-item' + (open ? ' open' : '')}>
      <div className="ug-faq-q" onClick={function () { setOpen(!open); }}>
        <span>Q. {props.q}</span>
        <span className="ug-faq-toggle">+</span>
      </div>
      <div className="ug-faq-a">{props.a}</div>
    </div>
  );
}

// ── 메인 페이지 ──
export default function UserGuide(props) {
  var _p = useState(0);
  var progress = _p[0];
  var setProgress = _p[1];

  // 스크롤 진행률 계산
  useEffect(function () {
    function onScroll() {
      var h = document.documentElement;
      var scrollTop = h.scrollTop || document.body.scrollTop;
      var scrollHeight = h.scrollHeight - h.clientHeight;
      var pct = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, pct)));
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return function () { window.removeEventListener('scroll', onScroll); };
  }, []);

  return (
    <div className="ug-root" style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 24px 60px' }}>
      {/* 상단 스크롤 진행바 */}
      <div className="ug-progress"><div className="ug-progress-bar" style={{ width: progress + '%' }} /></div>

      {/* Hero */}
      <section className="ug-hero">
        <div className="ug-hero-sub">AUTODEX 이용가이드</div>
        <h1>5분 만에 시작하는{"\n"}중고차 수출 자동화</h1>
        <p>
          차량 등록부터 말소·면장·인보이스·세무까지,{' '}
          OCR과 AI로 서류 작업을 90% 단축합니다.
        </p>
      </section>

      {/* 빠른 시작 3단계 */}
      <Reveal>
        <div className="ug-section-head">
          <h2>빠른 시작 3단계</h2>
          <p>로그인부터 첫 차량 등록까지, 단 3단계면 충분합니다</p>
        </div>
      </Reveal>
      <div className="ug-steps">
        <Reveal delay={0}>
          <StepCard num={1} icon="🔐"
            title="로그인 & 업체 설정"
            desc="계정 로그인 후 설정 > 업체 정보에서 사업자등록증·서명·도장을 업로드합니다." />
        </Reveal>
        <Reveal delay={0.1}>
          <StepCard num={2} icon="📸"
            title="차량 등록증 촬영"
            desc="등록증을 촬영하면 OCR이 차량번호·VIN·소유자 등을 자동 입력합니다." />
        </Reveal>
        <Reveal delay={0.2}>
          <StepCard num={3} icon="🚀"
            title="자동 서류 생성"
            desc="매매계약서·말소신청서·품의서가 한 번에 생성되어 다운로드 가능합니다." />
        </Reveal>
      </div>

      {/* 기능별 섹션 */}
      <Reveal>
        <div className="ug-section-head">
          <h2>주요 기능</h2>
          <p>중고차 수출 업무 전 과정을 한 플랫폼에서</p>
        </div>
      </Reveal>

      <Feature icon="📝" title="차량 등록 OCR" tag="1단계"
        desc="등록증 사진만 찍으면 차량번호·차대번호·소유자·주소·주민번호·주행거리가 자동 추출됩니다."
        bullets={[
          'Claude / GPT / Clova 4가지 OCR 엔진 자동 선택',
          '주민번호는 AES 암호화 저장, 화면 마스킹',
          '수기 입력 모드도 지원',
        ]} />

      <Feature icon="🗂️" reverse title="말소대행 자동화" tag="2단계"
        desc="말소대행 신청 한 번이면 대행업체에 자동 전달되고, 말소증 업로드 시 OCR로 완료 처리됩니다."
        bullets={[
          '말소신청서 PDF 자동 생성 (별지 제17호 서식)',
          '업체별 도장/서명 자동 삽입',
          '말소 완료 시 수출 기한(+270일) 자동 계산',
        ]} />

      <Feature icon="🧾" title="인보이스 & 관세사 전송" tag="3단계"
        desc="차량을 묶어 인보이스를 생성하고 관세사에 이메일 자동 발송. 제목엔 업체·목적국·선적방식이 포함됩니다."
        bullets={[
          '컨테이너선/로로선 혼합 인보이스 지원',
          'USD/KRW 다통화 + 환율 자동 적용',
          'Excel 포맷 + 업체 서명 이미지 삽입',
        ]} />

      <Feature icon="🔍" reverse title="평가사 모바일 검차" tag="선택"
        desc="평가사 전용 앱에서 도막 OCR, 부위별 손상 체크, 사진 업로드, 매입 결정까지 현장 완결."
        bullets={[
          '별지 제82호서식 4면도 기반 사고부위 마킹',
          '도막 측정기 사진 → AI가 수치 자동 추출',
          '매입 결정 시 AUTODEX에 차량 자동 등록',
        ]} />

      <Feature icon="💰" title="세무처리 & 부가세 환급" tag="4단계"
        desc="세금계산서·구매확인서를 업로드하면 자동 매칭되고 부가세 환급 금액이 자동 계산됩니다."
        bullets={[
          '차량별 부가세/공급가액 분리',
          '장부관리 자동 연동',
          '엑셀 내보내기',
        ]} />

      <Feature icon="🏛️" reverse title="경매장 (베타)" tag="신규"
        desc="수출업체 간 차량 경매 플랫폼. 자유 가격 입찰, 즉시 마감, 낙찰 후 연락처 자동 교환까지."
        bullets={[
          '실시간 입찰 (WebSocket)',
          '낙찰 시 전화번호·주소 공유 카드',
          '10분 후 취소 경매 자동 정리',
        ]} />

      {/* FAQ */}
      <Reveal>
        <div className="ug-section-head">
          <h2>자주 묻는 질문</h2>
          <p>처음 사용하신다면 먼저 확인해보세요</p>
        </div>
      </Reveal>
      <div className="ug-faq">
        <Reveal delay={0}><FAQItem q="OCR 인식이 잘못 될 때는 어떻게 하나요?"
          a="OCR 결과 필드 옆 신뢰도 표시를 확인하고 직접 수정할 수 있습니다. 재촬영도 가능하며, 차량 정보가 틀린 경우 수기 입력 모드로 전환하세요." /></Reveal>
        <Reveal delay={0.05}><FAQItem q="서류 PDF에 도장/서명이 자동으로 들어가나요?"
          a="설정 > 업체 정보에서 서명·도장 이미지를 한 번 업로드하면 매매계약서·말소신청서 등에 자동 삽입됩니다. 관세사용 인보이스에는 서명만 들어가고 도장은 들어가지 않습니다." /></Reveal>
        <Reveal delay={0.1}><FAQItem q="여러 업체를 하나의 계정으로 관리할 수 있나요?"
          a="일반 사용자는 최대 5개, 서류대행업체는 무제한으로 업체를 관리할 수 있습니다. 상단 업체 드롭다운에서 바로 전환됩니다." /></Reveal>
        <Reveal delay={0.15}><FAQItem q="모바일에서도 사용 가능한가요?"
          a="웹은 모바일 브라우저에서도 동작합니다. 평가사의 경우 전용 앱(autodex-app)에서 검차 기능을 사용하실 수 있습니다." /></Reveal>
        <Reveal delay={0.2}><FAQItem q="기존 엑셀 데이터를 가져올 수 있나요?"
          a="현재는 차량 등록 단위로 개별 입력 또는 OCR 입력만 지원합니다. 대량 마이그레이션이 필요하면 문의 주세요." /></Reveal>
      </div>

      {/* 하단 CTA */}
      <Reveal>
        <div className="ug-cta">
          <h2>도움이 더 필요하신가요?</h2>
          <p>사용 중 막히는 부분이나 개선 요청은 언제든 알려주세요</p>
          <a className="ug-cta-btn" href="mailto:support@autodex.kr">문의하기</a>
        </div>
      </Reveal>
    </div>
  );
}
